import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { env } from "../env";
import { useAuthStore } from "@/features/auth/stores/authStore";

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  const lang = document.documentElement.lang || "pt-BR";
  config.headers.set("Accept-Language", lang);
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }

    const store = useAuthStore.getState();
    original._retried = true;
    // O refresh token vive no cookie HttpOnly `rt` — não há como checá-lo no JS;
    // tentamos renovar e deixamos o servidor decidir (cookie ausente/expirado → 401).
    // Renovação single-flight compartilhada — vive no authStore (o SSE usa a mesma).
    const refreshed = await store.refreshSession();
    if (!refreshed) {
      return Promise.reject(error);
    }

    const newToken = useAuthStore.getState().accessToken;
    if (newToken) {
      original.headers.set("Authorization", `Bearer ${newToken}`);
    }
    return apiClient(original);
  },
);
